 
import React, { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Flag from 'react-country-flag'; // npm install react-country-flag
 
const API_URL = "https://api.securxperts.com:8000";
const ENDPOINT = "/forms/contact";
 
// ====================== COUNTRIES DATA ======================
const COUNTRIES = [
  { code: "+91", name: "India", iso: "IN" },
  { code: "+1",  name: "United States", iso: "US" },
  { code: "+44", name: "United Kingdom", iso: "GB" },
  { code: "+61", name: "Australia", iso: "AU" },
  { code: "+971", name: "United Arab Emirates", iso: "AE" },
  // Add more countries if needed
];
 
// ====================== COUNTRY DROPDOWN ======================
const CountryDropdown = ({ value, onChange, error, touched }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef(null);
 
  const filteredCountries = COUNTRIES.filter(
    (c) => c.code.includes(search) || c.name.toLowerCase().includes(search.toLowerCase())
  );
 
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
  const selected = COUNTRIES.find((c) => c.code === value) || null;
 
  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full p-3 rounded-lg border cursor-pointer flex items-center gap-2 transition-all ${
          error && touched
            ? "border-orange-400 bg-orange-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected ? (
          <>
            <Flag countryCode={selected.iso} svg style={{ width: '24px', height: '16px', borderRadius: '2px' }} />
            <span className="font-medium">{selected.code}</span>
          </>
        ) : (
          <span className="text-gray-500">Country Code</span>
        )}
        <span className="ml-auto text-gray-400">▼</span>
      </div>
 
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search country or code..."
            className="w-full p-2 border-b focus:outline-none"
            autoFocus
          />
          {filteredCountries.map((country) => (
            <div
              key={country.code}
              className="p-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
              onClick={() => {
                onChange({ target: { name: "country_code", value: country.code } });
                setIsOpen(false);
                setSearch("");
              }}
            >
              <Flag countryCode={country.iso} svg style={{ width: '24px', height: '16px', borderRadius: '2px' }} />
              <span>{country.code}</span>
              <span className="text-gray-500 text-sm ml-2">{country.name}</span>
            </div>
          ))}
        </div>
      )}
 
      {error && touched && <p className="mt-1 text-sm text-orange-600">{error}</p>}
    </div>
  );
};
 
// ====================== SUCCESS POPUP ======================
const SuccessPopup = ({ isOpen, onClose }) => {
  const timeoutRef = useRef(null);
 
  useEffect(() => {
    if (isOpen) {
      timeoutRef.current = setTimeout(onClose, 5000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isOpen, onClose]);
 
  if (!isOpen) return null;
 
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 max-w-sm w-full shadow-2xl relative animate-in fade-in zoom-in duration-200">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-2xl"
        >
          ×
        </button>
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold mb-2 text-green-600">Success!</h3>
          <p className="text-gray-600">Your request has been Submitted Successfully.</p>
        </div>
      </div>
    </div>
  );
};
 
/* ----------------------------------------------------
   IMPROVED CONTACT FORM
----------------------------------------------------- */
const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    country_code: "",
    phoneno: "",
    location: "",
    purpose: "",
    message: "",
  });
 
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
 
  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        return "";
      case "email":
        if (!value.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Invalid email";
        return "";
      case "country_code":
        if (!value) return "Country code is required";
        return "";
      case "phoneno":
        if (!value.trim()) return "Phone number is required";
        if (value.length !== 10) return "Must be exactly 10 digits";
        return "";
      case "location":
      case "purpose":
      case "message":
        if (!value.trim()) return "This field is required";
        return "";
      default:
        return "";
    }
  };
 
  const handleChange = (e) => {
    const { name, value } = e.target;
 
    let newValue = value;
 
    // Phone number cleaning
    if (name === "phoneno") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }
 
    // Name / location / purpose - only letters + spaces
    if (["name", "location", "purpose"].includes(name)) {
      newValue = value.replace(/[^a-zA-Z\s]/g, "");
    }
 
    setForm((prev) => ({ ...prev, [name]: newValue }));
    setTouched((prev) => ({ ...prev, [name]: true }));
 
    const error = validateField(name, newValue);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };
 
  // Check form validity
  useEffect(() => {
    const requiredFields = ["name", "email", "country_code", "phoneno", "location", "purpose", "message"];
    const allFilled = requiredFields.every((f) => form[f]?.trim());
    const noErrors = Object.values(errors).every((e) => !e);
    setIsValid(allFilled && noErrors);
  }, [form, errors]);
 
  const handleSubmit = async (e) => {
    e.preventDefault();
 
    if (!isValid) return;
 
    setSubmitting(true);
 
    try {
      const resp = await fetch(`${API_URL}${ENDPOINT}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
 
      if (resp.ok) {
        setShowSuccess(true);
        // Reset form
        setForm({
          name: "", email: "", country_code: "", phoneno: "",
          location: "", purpose: "", message: "",
        });
        setErrors({});
        setTouched({});
      } else {
        alert("Submission failed. Please try again.");
      }
    } catch (err) {
      alert("Network error. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };
 
  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="max-w-3xl mx-auto bg-white p-6 md:p-10 rounded-2xl shadow-lg border"
      >
        <h3 className="text-3xl font-bold text-center mb-8">
          Request a Trial or Live Demo
        </h3>
 
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Name */}
          <div>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Full Name *"
              maxLength={60}
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.name && touched.name ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.name && touched.name && (
              <p className="mt-1 text-sm text-orange-600">{errors.name}</p>
            )}
          </div>
 
          {/* Email */}
          <div>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              maxLength={60}
              placeholder="Email *"
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.email && touched.email ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.email && touched.email && (
              <p className="mt-1 text-sm text-orange-600">{errors.email}</p>
            )}
          </div>
 
          {/* Country Code */}
          <div>
            <CountryDropdown
              value={form.country_code}
              onChange={handleChange}
              error={errors.country_code}
              touched={touched.country_code}
            />
          </div>
 
          {/* Phone */}
          <div>
            <input
              name="phoneno"
              value={form.phoneno}
              onChange={handleChange}
              placeholder="Phone Number *"
              maxLength={10}
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.phoneno && touched.phoneno ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.phoneno && touched.phoneno && (
              <p className="mt-1 text-sm text-orange-600">{errors.phoneno}</p>
            )}
          </div>
 
          {/* Location */}
          <div className="md:col-span-2">
            <input
              name="location"
              value={form.location}
              onChange={handleChange}
              maxLength={60}
              placeholder="Organisation Location *"
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.location && touched.location ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.location && touched.location && (
              <p className="mt-1 text-sm text-orange-600">{errors.location}</p>
            )}
          </div>
 
          {/* Purpose */}
          <div className="md:col-span-2">
            <input
              name="purpose"
              value={form.purpose}
              onChange={handleChange}
              maxLength={60}
              placeholder="Purpose (Campus, Corporate, Training) *"
              className={`w-full p-3 rounded-lg border outline-none transition-all ${
                errors.purpose && touched.purpose ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.purpose && touched.purpose && (
              <p className="mt-1 text-sm text-orange-600">{errors.purpose}</p>
            )}
          </div>
 
          {/* Message */}
          <div className="md:col-span-2">
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us about your hiring process *"
              rows={4}
              className={`w-full p-3 rounded-lg border outline-none transition-all resize-none ${
                errors.message && touched.message ? "border-orange-400" : "border-gray-300 hover:border-gray-400"
              }`}
            />
            {errors.message && touched.message && (
              <p className="mt-1 text-sm text-orange-600">{errors.message}</p>
            )}
          </div>
        </div>
 
        <button
          type="submit"
          disabled={submitting || !isValid}
          className={`w-full mt-8 py-3 rounded-full text-white font-medium transition-all ${
            isValid && !submitting
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          {submitting ? "Submitting..." : "Request a Trial"}
        </button>
      </form>
 
      {/* Success Popup */}
      <SuccessPopup
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
};
export default function Kpi() {
  const SCROLL_SHOW_THRESHOLD = 300;
 
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
 
  const scrollTimeoutRef = useRef(null);
 
  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset =
    CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;
 
  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);
 
    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;
 
    setScrollProgress(Math.min(100, Math.max(0, progress)));
    setIsScrolling(true);
 
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
 
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, 250);
  }, []);
 
  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Force scroll to top on component mount with multiple attempts
    const forceScrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (window.scrollY !== 0) {
        // If still not at top, try again after a short delay
        setTimeout(forceScrollToTop, 50);
      }
    };
    
    forceScrollToTop();
    
    // Additional fallback for stubborn browsers
    setTimeout(forceScrollToTop, 100);
    setTimeout(forceScrollToTop, 300);
    
    onScroll();

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      // Restore scroll restoration when component unmounts
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);
 
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
 
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };
 
  const features = [
  {
    title: " Live Productivity Dashboards",
    description: " Monitor workforce activity with real-time visualisations of login/logout patterns, application usage, and idle periods, enabling managers to quickly assess productivity and take timely action to optimise team performance.",
  },
  {
    title: " Advanced Activity Heatmaps",
    description: " Interactive heatmaps provide insights into team efficiency, highlighting high-performing employees and identifying areas that require additional support, helping managers make informed decisions for operational improvement.",
  },
  {
    title: "Automated Event Tracking",
    description: " System events such as restarts, shutdowns, sleep cycles and session histories are automatically recorded, providing complete transparency and enabling IT teams to track operational consistency across all devices.",
  },
  {
    title: " GDPR-Compliant Data Management",
    description: " All user activity, event logs and application tracking are securely stored and fully compliant with GDPR and other data protection regulations, ensuring privacy while maintaining operational visibility and audit readiness.",
  },
  {
    title: "Role-Based Access Controls",
    description: " Grant customised access levels to administrators, managers and employees, allowing each user to view relevant insights securely, ensuring data confidentiality while enabling actionable, role-specific decision-making.",
  },
  {
    title: "Exportable Analytics & Reports",
    description: " Generate and download detailed reports in Excel, PDF or CSV formats, covering productivity, application usage and workforce activity, supporting audits, performance reviews and strategic workforce planning initiatives.",
  },
];
 
  return (
    <div className="w-full font-sans bg-white text-[#063852]">
<Navbar/>
      {/* =========================================================
          HERO SECTION
      ========================================================== */}
      <section className="w-full bg-white pb-20 px-6 md:px-20 flex flex-col md:flex-row items-center justify-center gap-10 py-3">
         {/* Logo Placeholder
  <div className="absolute top-10 left-6 md:top-5 md:left-16 z-20 ">
    <img
      src="/img/logosphare.png"   // <-- Replace with your logo file
      alt="aroganarayan logo"
      className="w-32 md:w-40 object-contain"
    />
    </div> */}
       

          {/* LEFT TEXT */}
          <div className="w-full md:w-1/2 text-start md:text-left">
             <img
            src="/img/84.png"
            alt="Logo"
            className="w-32 mb-6   md:mx-0"
          />
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
             Intelligent Device & Workforce Monitoring
             

            </h1>
            <p className="text-gray-600 mb-8 text-sm md:text-base">
LogSphere is an intelligent workforce and device monitoring software designed to track employee productivity, application usage, system events and user activity in real time. It delivers actionable insights for HR teams, IT administrators and business leaders to improve efficiency, ensure accountability and support data-driven decisions.
            </p>

            {/* <div className="flex gap-4">
              <button className="px-6 py-3 bg-[#0066cc] text-white rounded-lg">
                View Tutorials
              </button>
              <button className="px-6 py-3 bg-white border border-gray-300 rounded-lg">
                Book a Demo
              </button>
            </div> */}
          </div>

          {/* RIGHT IMAGE */}
          <div className="w-full md:w-1/2 flex justify-center">
            <img
  src="/img/logsphereheroimg.png"
  alt="KPI dashboard showing real-time business performance metrics"
  className="w-full max-w-2xl mx-auto h-auto rounded-xl mt-10 shadow-md"
/>
          </div>
       
      </section>
 
      {/* =========================================================
    VIDEO SECTION
========================================================== */}
<section className="w-full pb-20 px-6 md:px-0 bg-gray-50 py-5">
  <div className="max-w-7xl mx-auto">

    <div className="max-w-3xl mx-auto mb-10 text-center">
      <h2 className="text-3xl font-bold text-[#063852]">
        Effortless Monitoring in Action
      </h2>
      <p className="text-gray-600 mt-3">
        Experience a powerful monitoring dashboard that tracks devices and workforce
        activity in real time, ensuring transparency and operational control.
        LogSphere simplifies daily monitoring, boosts productivity and keeps your
        organization running smoothly.
      </p>
    </div>

    <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">

      {/* NATIVE BROWSER VIDEO ONLY */}
      <video
        className="w-full h-auto max-h-[220px] sm:max-h-[360px] md:max-h-none block"
        src="/video/logsphere.mp4"
        muted
        playsInline
        autoPlay
        loop
        preload="auto"
        controls
      >
        Your browser does not support HTML5 video.
      </video>

    </div>
  </div>
</section>

 
      {/* =========================================================
          ALL-IN-ONE MONITORING SOLUTION
      ========================================================== */}
      <section className="w-full py-16 px-6 md:px-16 bg-[#f8fbff]">
        <h2 className="text-3xl font-bold text-center mb-12">
        Optimised Employee Engagement Experience
 
        </h2>
 
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
 
          {/* CARD 1 */}
          <div className="p-6 bg-white rounded-xl shadow text-center">
            <img src="/img/timer.png" alt=" Employee activity insights showing active and idle periods" className="mx-auto mb-4 w-14" />
            <h3 className="font-bold text-xl mb-2">Instant Activity Insights
</h3>
            <p className="text-gray-600 text-sm">
               Track active sessions, idle periods and app usage for timely, informed decisions.
 
            </p>
          </div>
 
          {/* CENTER CARD BIG */}
          <div className="p-8 bg-white  rounded-xl shadow text-center">
            <img src="/img/crossdevice.png" alt="Cross-device monitoring across Windows macOS Linux" className="mx-auto mb-4 w-14" />
            <h3 className="font-bold text-xl mb-2">Cross-Device Monitoring
</h3>
            <p className="text-gray-600  text-sm">
              Monitor Windows, macOS and Linux devices seamlessly with accurate reporting.
 
            </p>
          </div>
 
          {/* CARD 3 */}
          <div className="p-6 bg-white rounded-xl shadow text-center">
            <img src="/img/rolebased.png" alt=" Role-based dashboards for managers and employees" className="mx-auto mb-4 w-14" />
            <h3 className="font-bold text-xl mb-2">Role-Based Dashboards
</h3>
            <p className="text-gray-600 text-sm">
              Managers, leads and staff access only relevant analytics for efficient oversight.
 
            </p>
          </div>
 
          {/* CARD 4 */}
          <div className="p-6 bg-white rounded-xl shadow text-center">
            <img src="/img/audit.png" alt=" Audit and compliance tracking with system event logs" className="mx-auto mb-4 w-14" />
            <h3 className="font-bold text-xl mb-2">Audit & Compliance Tracking
</h3>
            <p className="text-gray-600 text-sm">
              Aggregate Logs system events including restarts, sleep/resume cycles and session histories in a secure, central repository for efficient oversight and historical reference.
 
            </p>
          </div>
 
          {/* CARD 5 */}
          <div className="p-6 bg-white rounded-xl shadow text-center">
            <img src="/img/improvedhr.png" alt="Application usage analytics with productivity classification" className="mx-auto mb-4 w-14" />
            <h3 className="font-bold text-xl mb-2">Advanced Application Insights
 
</h3>
            <p className="text-gray-600 text-sm">
             Capture metrics for software and browser sessions, covering duration, usage frequency, and productivity classification for accurate performance monitoring.
 
            </p>
          </div>
 
          {/* CARD 6 */}
          <div className="p-6 bg-white rounded-xl shadow text-center">
            <img src="/img/datadriven.png" alt=" Productivity heatmap and performance reports" className="mx-auto mb-4 w-14" />
            <h3 className="font-bold text-xl mb-2">Data-Driven Performance Reports
</h3>
            <p className="text-gray-600 text-sm">
             Generate daily, weekly, or monthly productivity heatmaps and export reports in Excel, PDF or CSV to support management analysis and review.
 
            </p>
          </div>
        </div>
      </section>
 
      {/* =========================================================
          KEEP MONITORING EVERYWHERE YOU GO
      ========================================================== */}
      <section className="w-full py-20 px-6 md:px-16 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
         
          <div>
            <img
              src="/img/workforcemonitoringfromanywhere.png"
              alt="Monitoring dashboard accessible on mobile tablet and laptop"
              className="w-full"
            />
          </div>
 
          <div>
            <h2 className="text-3xl font-bold mb-4">
              Keep Monitoring <br /> Everywhere You Go
            </h2>
            <p className="text-gray-600 mb-6">
              Access your monitoring dashboard from your phone, tablet, or laptop.
              Stay updated anytime, anywhere.
            </p>
            {/* <button className="px-6 py-3 bg-[#0066cc] text-white rounded-lg">
              Sign Up Now
            </button> */}
          </div>
        </div>
      </section>
 
      {/* =========================================================
          ADVANCED FEATURES
      ========================================================== */}
    <section className="w-full py-20 px-6 md:px-16 bg-[#0099e6] text-white">
  <h2 className="text-3xl font-bold text-center mb-10">
    Key Features of Log Sphere
  </h2>
 
  <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
    {features.map((item, i) => (
      <div
        key={i}
        className="p-6 bg-white text-[#063852] rounded-xl shadow text-center"
      >
        {/* 6 Different Icon Placeholders */}
        {i === 0 && <img src="/img/liveproductivity.png" alt=" Live productivity dashboard with real-time visualisations" className="mx-auto mb-4 w-12" />}
        {i === 1 && <img src="/img/heatmaps.png" alt=" Advanced activity heatmap for workforce efficiency" className="mx-auto mb-4 w-12" />}
        {i === 2 && <img src="/img/automatedevent.png" alt="Automated system event tracking dashboard" className="mx-auto mb-4 w-12" />}
        {i === 3 && <img src="/img/gdpr (3).png" alt=" GDPR compliant workforce monitoring data security" className="mx-auto mb-4 w-12" />}
        {i === 4 && <img src="/img/rolebased (1).png" alt=" Role-based access control for monitoring platform" className="mx-auto mb-4 w-12" />}
        {i === 5 && <img src="/img/exportable.png" alt=" Exportable workforce analytics reports" className="mx-auto mb-4 w-12" />}
 
        <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
        <p className="text-gray-600 text-sm">{item.description}</p>
      </div>
    ))}
  </div>
</section>
 
      <section className="py-16 px-6">
      <ContactForm />
      </section>
      <Footer/>  
      {/* Scroll to Top Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer
          flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r
          from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>
 
          <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-white relative z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
 
 